import { createUseStyles } from "react-jss";
import * as Mixins from "../../../theme/Mixins";
import type { Theme } from "../../../types/Theme";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";
import { resolveTextInputBorderColor } from "../../../theme/inputThemeTokens";
import {
    CHOICE_FOCUS_OUTLINE_OFFSET_PX,
    resolveModernChoiceFocusOutline,
} from "../../../theme/choiceThemeTokens";
/**
 * Modern star colour: filled/preview stars take the brand selected colour; empty stars take the
 * text-input border token (gray), or the error colour when the question is in validation error so the
 * unanswered stars flag what's missing. Legacy is unchanged (always PrimaryColor; fill conveyed only
 * by the solid/outline icon swap).
 */
function resolveStarColor(theme: Theme, filled?: boolean): string {
    if (!shouldUseModernTheme(theme)) {
        return theme.PrimaryColor;
    }
    if (filled) {
        return theme.PrimaryColor;
    }
    return resolveTextInputBorderColor(theme);
}

export default createUseStyles({
    starRating: {
        composes: "star-rating",
        display: "flex",
        alignItems: "center",
        touchAction: "none",
        userSelect: "none",
        MsUserSelect: "none",
    },
    starRatingSpacer: {
        composes: "star-rating__spacer",
        verticalAlign: "middle",
        height: ({ starHeight }) => starHeight,
        width: 7,
    },
    starRatingStar: ({ theme, splitWidth, starHeight, filled }) => ({
        color: resolveStarColor(theme, filled),
        verticalAlign: "middle",
        composes: "star-rating__star",
        height: starHeight,
        overflow: "hidden",
        position: "relative",
        width: splitWidth,
    }),
    /**
     * Modern keyboard focus halo, applied as an extra class only when UseModernTheme is on so legacy
     * keeps its default focus outline. The ring colour tracks the star's state — gray when empty,
     * primary when filled, error colour when the question is invalid.
     */
    starRatingStarModernFocus: {
        composes: "star-rating__star--modern-focus",
        "&:focus-visible": {
            borderRadius: ({ theme }) => theme.BorderRadius,
            outline: ({ theme, filled }) =>
                resolveModernChoiceFocusOutline(theme, filled),
            outlineOffset: CHOICE_FOCUS_OUTLINE_OFFSET_PX,
        },
    },
    disabled: {
        opacity: 0.5,
        pointerEvents: "none",
    },
    /** Modern clear control: borderless 28×28 hit target; legacy uses default Button chrome. */
    clearButtonModern: {
        composes: "star-rating__clear--modern",
        alignItems: "center",
        background: "transparent",
        border: "none",
        // Same Button element as legacy, which takes its radius from Field Settings → Corners.
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        boxShadow: "none",
        boxSizing: "border-box",
        color: ({ theme }: { theme: Theme }) => theme.BlackColor,
        cursor: "pointer",
        display: "flex",
        fontFamily: ({ theme }: { theme: Theme }) => `"${theme.TextFont}", sans-serif`,
        height: 28,
        justifyContent: "center",
        minHeight: 28,
        minWidth: 28,
        outline: "none",
        padding: 0,
        width: 28,
        "&:hover": {
            background: ({ theme }: { theme: Theme }) => Mixins.alpha(theme.NeutralDarkColor, 0.08),
            boxShadow: "none",
        },
        "&:focus-visible": {
            boxShadow: "none",
            outline: ({ theme }: { theme: Theme }) =>
                resolveModernChoiceFocusOutline(theme, false),
            outlineOffset: CHOICE_FOCUS_OUTLINE_OFFSET_PX,
        },
        "& .button__container": {
            height: 16,
            lineHeight: "16px",
            margin: 0,
        },
        "& .button__icon": {
            left: 0,
            position: "initial",
            right: 0,
        },
    },
});
